import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'

import {LABELS} from '../constants'
import {useSavedViewEndpoints} from '../contexts/SavedViewEndpointsContext'
import type {UpdateSavedViewResponse, UpdateSavedViewVariables} from '../types'
import {
  cancelSavedViewRead,
  invalidateSavedView,
  primeSavedViewCaches,
  type SavedViewMutationOptions,
  sendSavedViewMutationJson,
} from '../utils/saved-view-mutations'

export function useUpdateSavedViewMutation<TConfiguration = never>({owner, repo}: SavedViewMutationOptions) {
  const queryClient = useQueryClient()
  const {basePath, listUrl} = useSavedViewEndpoints(owner, repo)

  return useMutation<UpdateSavedViewResponse<TConfiguration>, Error, UpdateSavedViewVariables<TConfiguration>>({
    onMutate: ({id}) => cancelSavedViewRead({queryClient, owner, repo, basePath, listUrl}, id),
    mutationFn: ({id, ...body}) =>
      sendSavedViewMutationJson<UpdateSavedViewResponse<TConfiguration>>(
        `${basePath}/${encodeURIComponent(id)}.json`,
        {method: 'PUT', body},
        LABELS.views.updateError,
      ),
    onSuccess: ({savedView}) => primeSavedViewCaches({queryClient, owner, repo, basePath, listUrl}, savedView),
    onError: (_error, {id}) => {
      // Refetches the read `onMutate` canceled; on success the PUT response replaces it.
      invalidateSavedView({queryClient, owner, repo, basePath, listUrl}, id)
      announce(LABELS.views.updateError, {assertive: true})
    },
  })
}
